import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { accountsAPI, journalAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, getTodayJalali, jalaliToGregorianStr } from "../../../utils/PersianDatePicker";
import {
  Plus, Edit2, Trash2, EyeOff, Eye, FileText,
  ChevronDown, ChevronLeft, X, Printer, Search,
} from "lucide-react";

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
  currency: string;
  parent_id: number | null;
  balance: number;
  description: string;
  is_active: number;
  created_at?: string;
  opening_balance?: number | null;  // 🆕 از جدول جدید account_opening_balances
  opening_date?: string | null;     // 🆕 تاریخ بیلانس اولیه
}

const TYPES: Record<string, string> = {
  cash_asset:     "دارایی نقدی",
  non_cash_asset: "دارایی غیر نقدی",
  income:         "عواید",
  expense:        "مصارف",
  liability:      "قرضداری",
  equity:         "سهم شرکا",
};

const TYPE_COLORS: Record<string, string> = {
  cash_asset:     "#10b981",
  non_cash_asset: "#3b82f6",
  income:         "#8b5cf6",
  expense:        "#ef4444",
  liability:      "#f59e0b",
  equity:         "#06b6d4",
};

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

const fmt = (n: number, cur?: string) => {
  const s = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n || 0));
  return (n < 0 ? "-" : "") + s + (cur ? " " + cur : "");
};

export default function AccountsPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [accounts, setAccounts]       = useState<Account[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [typeFilter, setTypeFilter]   = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showAdd, setShowAdd]         = useState(false);
  const [editAcc, setEditAcc]         = useState<Account | null>(null);
  const [reportAcc, setReportAcc]     = useState<Account | null>(null);
  const [reportData, setReportData]   = useState<any[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo,   setReportDateTo]   = useState("");
  const [toast, setToast]             = useState({ text: "", ok: true });

  const [form, setForm] = useState({
    name: "", type: "", currency: "", parent_id: "",
    opening_balance: "0", created_date: todayGregorian(), description: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    type: "",
    currency: "",
    parent_id: "",
    opening_balance: "0",
    opening_date: todayGregorian(),
    change_opening_balance: false,  // 🆕 فقط وقتی true شد، بیلانس اولیه به سرور فرستاده می‌شود
  });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await accountsAPI.list();
    if (res.success) { setAccounts(res.accounts); setExpanded(new Set(res.accounts.filter((a: any) => !a.parent_id).map((a: any) => a.id))); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const parents   = accounts.filter(a => !a.parent_id);
  const children  = (pid: number) => accounts.filter(a => a.parent_id === pid);

  const parentOptions = parents
    .filter(a =>
      (!form.type || a.type === form.type) &&
      (!form.currency || a.currency === form.currency)
    )
    .map(a => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));

  // برای فرم ویرایش — حساب خودش را از لیست مادران ممکن حذف کن
  const editParentOptions = parents
    .filter(a =>
      a.id !== (editAcc?.id || 0) &&  // خود حساب نمی‌تواند مادر خودش باشد
      (!editForm.type || a.type === editForm.type) &&
      (!editForm.currency || a.currency === editForm.currency)
    )
    .map(a => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));

  const filtered = parents.filter(a =>
    (!search || a.name.includes(search) || a.code.includes(search)) &&
    (!typeFilter || a.type === typeFilter)
  );

  const handleAdd = async () => {
    if (!form.name || !form.type || !form.currency) {
      showToast("نام، نوعیت و ارز الزامی است", false); return;
    }
    const res = await accountsAPI.add({
      name:            form.name,
      type:            form.type,
      currency:        form.currency,
      parent_id:       form.parent_id ? parseInt(form.parent_id) : null,
      opening_balance: parseFloat(form.opening_balance || "0"),
      created_date:    form.created_date,
      description:     form.description,
    });
    if (res.success) {
      showToast(`حساب با کد ${res.code} ایجاد شد`);
      setShowAdd(false);
      setForm({ name: "", type: "", currency: "", parent_id: "", opening_balance: "0", created_date: todayGregorian(), description: "" });
      load();
    } else {
      showToast(res.message || "خطا در ایجاد حساب", false);
    }
  };

  const handleEdit = async () => {
    if (!editAcc) return;
    if (!editForm.name) { showToast("نام الزامی است", false); return; }

    // ساخت payload — فقط فیلدهایی که قابل ویرایش هستند
    const payload: any = {
      id: editAcc.id,
      name: editForm.name,
      description: editForm.description,
      type: editForm.type,
      currency: editForm.currency,
      parent_id: editForm.parent_id ? parseInt(editForm.parent_id) : null,
    };

    // 🆕 بیلانس اولیه فقط زمانی فرستاده می‌شود که کاربر صریحاً فعالش کرده باشد
    // این تضمین می‌کند که اگر کاربر فقط نام/توضیحات را تغییر داد،
    // هیچ رکوردی در جدول opening_balance ساخته/تغییر نمی‌کند
    if (editForm.change_opening_balance) {
      payload.opening_balance = parseFloat(editForm.opening_balance || "0");
      payload.opening_date = editForm.opening_date;
    }

    const res = await accountsAPI.update(payload);
    if (res.success) {
      showToast("حساب ویرایش شد");
      setEditAcc(null);
      load();
    } else {
      showToast(res.message || "خطا", false);
    }
  };

  // باز کردن مودال ویرایش با مقادیر فعلی حساب
  const openEditModal = (acc: Account) => {
    setEditAcc(acc);
    setEditForm({
      name: acc.name,
      description: acc.description || "",
      type: acc.type,
      currency: acc.currency,
      parent_id: acc.parent_id ? String(acc.parent_id) : "",
      // اگر در جدول جدید بیلانس اولیه نداشت، خالی نشون بده تا کاربر متوجه بشه
      opening_balance: acc.opening_balance != null ? String(acc.opening_balance) : "0",
      opening_date: acc.opening_date || acc.created_at?.split(" ")[0] || todayGregorian(),
      change_opening_balance: false,  // 🆕 به‌صورت پیش‌فرض دست نزن
    });
  };

  const handleDelete = async (acc: Account) => {
    if (!window.confirm(`حذف حساب "${acc.name}"؟`)) return;
    const res = await accountsAPI.delete(acc.id);
    if (res.success) { showToast("حساب حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleToggle = async (acc: Account) => {
    const res = await accountsAPI.toggleStatus(acc.id);
    if (res.success) load();
    else showToast(res.message || "خطا", false);
  };

  const handleReport = async (acc: Account, dateFrom?: string, dateTo?: string) => {
    setReportAcc(acc);
    setReportLoading(true);
    setReportData([]);
    const res = await accountsAPI.report(acc.id, dateFrom || reportDateFrom, dateTo || reportDateTo);
    if (res.success) setReportData(res.transactions || []);
    setReportLoading(false);
  };

  const applyDateFilter = () => {
    if (reportAcc) handleReport(reportAcc, reportDateFrom, reportDateTo);
  };

  const clearDateFilter = () => {
    setReportDateFrom("");
    setReportDateTo("");
    if (reportAcc) handleReport(reportAcc, "", "");
  };

  const setQuickDate = (type: string) => {
    const today = new Date();
    let from = "", to = todayGregorian();
    const pad = (n: number) => String(n).padStart(2, "0");
    const fmt2 = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    switch(type) {
      case "today":
        from = to = todayGregorian(); break;
      case "week": {
        const d = new Date(today); d.setDate(today.getDate() - 6);
        from = fmt2(d); break;
      }
      case "month": {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        from = fmt2(d); break;
      }
      case "3month": {
        const d = new Date(today); d.setMonth(today.getMonth() - 3);
        from = fmt2(d); break;
      }
      case "6month": {
        const d = new Date(today); d.setMonth(today.getMonth() - 6);
        from = fmt2(d); break;
      }
      case "year": {
        const d = new Date(today.getFullYear(), 0, 1);
        from = fmt2(d); break;
      }
    }
    setReportDateFrom(from);
    setReportDateTo(to);
    if (reportAcc) handleReport(reportAcc, from, to);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const printReport = () => {
    if (!reportAcc) return;
    const w = window.open("", "_blank");
    if (!w) return;

    const inItems  = reportData.filter(t => t.direction === "in");
    const outItems = reportData.filter(t => t.direction === "out");
    const totalIn  = inItems.reduce((s, t) => s + Math.abs(parseFloat(t.amount || 0)), 0);
    const totalOut = outItems.reduce((s, t) => s + Math.abs(parseFloat(t.amount || 0)), 0);
    const typeColor = TYPE_COLORS[reportAcc.type] || "#6366f1";

    // جمع به تفکیک ارز
    const currencyTotals: Record<string, { in: number; out: number }> = {};
    reportData.forEach(t => {
      const cur = t.currency || reportAcc.currency;
      if (!currencyTotals[cur]) currencyTotals[cur] = { in: 0, out: 0 };
      const amt = Math.abs(parseFloat(t.amount || 0));
      if (t.direction === "in") currencyTotals[cur].in += amt;
      else currencyTotals[cur].out += amt;
    });
    const currencySummaryHTML = Object.entries(currencyTotals).map(([cur, { in: i, out: o }]) => `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;min-width:140px;flex:1">
        <div style="font-size:10px;color:#64748b;font-weight:600;margin-bottom:4px">${cur}</div>
        <div style="font-size:11px;color:#059669;font-weight:700">▲ ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(i)}</div>
        <div style="font-size:11px;color:#dc2626;font-weight:700">▼ ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(o)}</div>
        <div style="font-size:11px;color:#1d4ed8;font-weight:800;border-top:1px solid #e2e8f0;margin-top:4px;padding-top:4px">= ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(i - o)}</div>
      </div>`).join("");

    // بیلانس تجمعی — شروع از بیلانس اولیه
    // اگر حساب در جدول جدید بیلانس اولیه ثبت‌شده دارد، از همان استفاده کن (دقیق و قابل اعتماد)
    // در غیر این صورت (حساب قدیمی)، رفتار قبلی حفظ می‌شود (محاسبه از روی balance فعلی)
    const openingBal = (reportAcc.opening_balance != null)
      ? Number(reportAcc.opening_balance)
      : reportAcc.balance - (reportData.reduce((s: number, t: any) => {
          const amt = Math.abs(parseFloat(t.amount || 0));
          return t.direction === "in" ? s + amt : s - amt;
        }, 0));
    let runBal = openingBal;

    const fmtN = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));

    const openingRow = `
      <tr style="background:#f0fdf4;border-bottom:2px solid #86efac">
        <td class="center" style="color:#94a3b8;font-size:10px">—</td>
        <td class="center" style="color:#059669;font-weight:700">${reportAcc.created_at ? gregorianToJalaliStr(reportAcc.created_at.split(" ")[0]) : "—"}</td>
        <td class="center" style="color:#059669;font-weight:700">—</td>
        <td colspan="2" style="font-weight:800;color:#059669;font-size:12px">🏦 بیلانس اولیه حساب</td>
        <td class="center">${reportAcc.currency}</td>
        <td class="center" style="font-weight:800;font-size:13px;color:#059669">+${fmtN(openingBal)}</td>
        <td class="center">—</td>
        <td>—</td>
        <td class="center" style="font-weight:800;font-size:13px;color:#059669">${fmtN(openingBal)}</td>
      </tr>`;

    const rows = (openingRow) + (reportData.length === 0
      ? `<tr><td colspan="9" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">هیچ تراکنشی ثبت نشده است</td></tr>`
      : reportData.map((txn: any, i: number) => {
          const amt = Math.abs(parseFloat(txn.amount || 0));
          if (txn.direction === "in") runBal += amt; else runBal -= amt;
          const isForeign = txn.orig_currency && txn.orig_currency !== txn.currency && txn.orig_currency !== "USD";
          const typeIcon = txn.direction === "in" ? "▲" : "▼";
          const typeColors: Record<string, string> = {
            "دریافت پول": "#3b82f6", "پرداخت پول": "#f97316",
            "مصارف": "#ef4444", "COGS / هزینه اجناس": "#8b5cf6",
            "انتقال به حساب": "#06b6d4", "انتقال از حساب": "#06b6d4",
            "فروش جنس": "#10b981", "فروش خدمت": "#10b981",
            "خرید جنس": "#f59e0b", "خرید خدمت": "#f59e0b",
          };
          const tColor = typeColors[txn.type] || "#64748b";
          const foreignCell = isForeign ? fmtN(txn.orig_amount) + " " + txn.orig_currency + " × " + parseFloat(txn.exchange_rate || 1).toFixed(2) : "—";
          const amtColor = txn.direction === "in" ? "#059669" : "#dc2626";
          const balColor = runBal >= 0 ? "#059669" : "#dc2626";
          return `
          <tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
            <td class="center" style="color:#94a3b8;font-size:10px">${i + 1}</td>
            <td class="center" style="white-space:nowrap">${gregorianToJalaliStr(txn.date)}</td>
            <td class="center" style="color:#7c3aed;font-weight:700;font-size:11px">${txn.serial || "—"}</td>
            <td>
              <span style="background:${tColor}18;color:${tColor};padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;white-space:nowrap">
                ${typeIcon} ${txn.type}
              </span>
            </td>
            <td style="max-width:180px">${txn.party || "—"}</td>
            <td class="center">${txn.currency || reportAcc.currency}</td>
            <td class="center" style="font-weight:800;font-size:12px;color:${amtColor}">
              ${txn.direction === "in" ? "+" : "−"}${fmtN(amt)}
            </td>
            <td class="center" style="font-size:10px;color:#64748b">${foreignCell}</td>
            <td style="max-width:160px;font-size:10px;color:#475569">${txn.notes || "—"}</td>
            <td class="center" style="font-weight:800;font-size:12px;color:${balColor}">
              ${fmtN(runBal)}
            </td>
          </tr>`;
        }).join(""));

    w.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="fa">
<head>
  <meta charset="UTF-8">
  <title>گزارش حساب — ${reportAcc.name}</title>
  <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4 landscape; margin: 12mm 14mm; }
    body { font-family: 'Vazirmatn', Tahoma, sans-serif; direction: rtl; font-size: 11px; color: #1e293b; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 20px; }

    /* هدر */
    .header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 16px; border-bottom: 3px solid ${typeColor}; margin-bottom: 16px; }
    .header-right { display: flex; align-items: center; gap: 14px; }
    .logo { width: 46px; height: 46px; background: linear-gradient(135deg, ${typeColor}, ${typeColor}99); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; }
    .header-title { font-size: 20px; font-weight: 800; color: #0f172a; }
    .header-sub { font-size: 11px; color: #64748b; margin-top: 2px; }
    .header-left { text-align: left; }
    .badge { display: inline-block; background: ${typeColor}; color: #fff; font-size: 10px; font-weight: 700; padding: 3px 12px; border-radius: 20px; margin-bottom: 4px; }
    .print-date { font-size: 10px; color: #94a3b8; }

    /* کارت‌های اطلاعات */
    .info-row { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
    .info-card { flex: 1; min-width: 100px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; }
    .info-card.accent { border-color: ${typeColor}50; background: ${typeColor}08; }
    .info-label { font-size: 9px; color: #94a3b8; font-weight: 700; margin-bottom: 3px; letter-spacing: 0.5px; }
    .info-value { font-size: 13px; font-weight: 800; color: #0f172a; }
    .info-card.balance .info-value { color: ${(reportAcc.balance || 0) >= 0 ? "#059669" : "#dc2626"}; }

    /* خلاصه ارزی */
    .currency-section { margin-bottom: 12px; }
    .currency-title { font-size: 11px; font-weight: 800; color: #374151; margin-bottom: 6px; padding-right: 8px; border-right: 3px solid ${typeColor}; }
    .currency-row { display: flex; gap: 8px; flex-wrap: wrap; }

    /* خلاصه کلی */
    .summary-row { display: flex; gap: 8px; margin-bottom: 16px; }
    .sum-box { flex: 1; border-radius: 10px; padding: 8px 12px; }
    .sum-box.in  { background: #f0fdf4; border: 1px solid #86efac; }
    .sum-box.out { background: #fff1f2; border: 1px solid #fca5a5; }
    .sum-box.net { background: #eff6ff; border: 1px solid #93c5fd; }
    .sum-box.cnt { background: #faf5ff; border: 1px solid #d8b4fe; }
    .sum-label { font-size: 9px; font-weight: 700; color: #64748b; margin-bottom: 3px; }
    .sum-value { font-size: 13px; font-weight: 800; }
    .sum-box.in  .sum-value { color: #059669; }
    .sum-box.out .sum-value { color: #dc2626; }
    .sum-box.net .sum-value { color: #1d4ed8; }
    .sum-box.cnt .sum-value { color: #7c3aed; }

    /* جدول */
    .table-title { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 8px; padding-right: 8px; border-right: 3px solid ${typeColor}; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; border-radius: 10px; overflow: hidden; }
    thead tr { background: linear-gradient(135deg, #1e293b, #334155); }
    th { padding: 9px 8px; text-align: right; font-weight: 700; color: #f1f5f9; white-space: nowrap; font-size: 10px; }
    th.center { text-align: center; }
    .row-even { background: #ffffff; }
    .row-odd  { background: #f8fafc; }
    td { padding: 7px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    td.center { text-align: center; }
    tr:last-child td { border-bottom: 2px solid #e2e8f0; }

    /* فوتر */
    .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-end; }
    .footer-note { font-size: 9.5px; color: #94a3b8; }
    .footer-sig { font-size: 10px; color: #64748b; border-top: 1px dashed #cbd5e1; padding-top: 6px; min-width: 160px; text-align: center; }

    @media print { body { font-size: 10px; } thead { display: table-header-group; } tr { page-break-inside: avoid; } }
  </style>
</head>
<body><div class="page">

  <!-- هدر -->
  <div class="header">
    <div class="header-right">
      <div class="logo">🏦</div>
      <div>
        <div class="header-title">${reportAcc.name}</div>
        <div class="header-sub">گزارش کامل تراکنش‌های حساب</div>
      </div>
    </div>
    <div class="header-left">
      <div><span class="badge">${TYPES[reportAcc.type] || reportAcc.type}</span></div>
      <div class="print-date">تاریخ چاپ: ${new Date().toLocaleDateString("fa-IR")} — ${new Date().toLocaleTimeString("fa-IR")}</div>
      ${reportDateFrom || reportDateTo ? `<div class="print-date" style="color:#8b5cf6;font-weight:600;margin-top:2px">📅 ${reportDateFrom ? gregorianToJalaliStr(reportDateFrom) : "ابتدا"} تا ${reportDateTo ? gregorianToJalaliStr(reportDateTo) : "انتها"}</div>` : ""}
    </div>
  </div>

  <!-- اطلاعات حساب -->
  <div class="info-row">
    <div class="info-card accent"><div class="info-label">کد حساب</div><div class="info-value">${reportAcc.code}</div></div>
    <div class="info-card"><div class="info-label">نوعیت</div><div class="info-value">${TYPES[reportAcc.type] || reportAcc.type}</div></div>
    <div class="info-card"><div class="info-label">ارز پایه</div><div class="info-value">${reportAcc.currency}</div></div>
    <div class="info-card balance accent"><div class="info-label">موجودی فعلی</div><div class="info-value">${fmt(reportAcc.balance, reportAcc.currency)}</div></div>
    <div class="info-card"><div class="info-label">تعداد تراکنش</div><div class="info-value" style="color:#7c3aed">${reportData.length}</div></div>
  </div>

  <!-- خلاصه کلی -->
  <div class="summary-row">
    <div class="sum-box in"><div class="sum-label">▲ جمع ورودی‌ها</div><div class="sum-value">+${fmt(totalIn, reportAcc.currency)}</div></div>
    <div class="sum-box out"><div class="sum-label">▼ جمع خروجی‌ها</div><div class="sum-value">−${fmt(totalOut, reportAcc.currency)}</div></div>
    <div class="sum-box net"><div class="sum-label">خالص گردش</div><div class="sum-value">${fmt(totalIn - totalOut, reportAcc.currency)}</div></div>
    <div class="sum-box cnt"><div class="sum-label">تراکنش‌ها</div><div class="sum-value">${reportData.length}</div></div>
  </div>

  <!-- خلاصه به تفکیک ارز -->
  ${Object.keys(currencyTotals).length > 1 ? `
  <div class="currency-section">
    <div class="currency-title">جمع به تفکیک ارز</div>
    <div class="currency-row">${currencySummaryHTML}</div>
  </div>` : ""}

  <!-- جدول -->
  <div class="table-title">جزئیات کامل تراکنش‌ها</div>
  <table>
    <thead>
      <tr>
        <th class="center">#</th>
        <th class="center">تاریخ</th>
        <th class="center">شماره سند</th>
        <th>نوع معامله</th>
        <th>طرف حساب / جزئیات</th>
        <th class="center">ارز</th>
        <th class="center">مبلغ</th>
        <th class="center">مبلغ اصلی × نرخ</th>
        <th>توضیحات</th>
        <th class="center">بیلانس</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <!-- فوتر -->
  <div class="footer">
    <div class="footer-note">این گزارش به صورت سیستمی تولید شده و نیازی به امضا ندارد.</div>
    <div class="footer-sig">امضا و مهر مسئول مالی</div>
  </div>

</div></body></html>`);

    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  // استایل‌های مشترک
  const card = {
    borderRadius: 14, padding: "14px 16px",
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
  };

  const inp = (val: string, onChange: (v: string) => void, ph?: string) => (
    <input value={val} onChange={e => onChange(e.target.value)} placeholder={ph}
      style={{
        width: "100%", padding: "10px 14px", borderRadius: 10,
        border: `1.5px solid ${T.border}`, background: T.input,
        color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
        boxSizing: "border-box", outline: "none",
      }} />
  );

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>

      {/* Toast */}
      {toast.text && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
          background: toast.ok ? "#10b981" : "#ef4444", color: "#fff",
          padding: "12px 28px", borderRadius: 12, fontSize: 13, fontWeight: 700,
          zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        }}>{toast.text}</div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجوی حساب..."
            style={{
              width: "100%", padding: "10px 36px 10px 14px", borderRadius: 10,
              border: `1.5px solid ${T.border}`, background: T.input,
              color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
              boxSizing: "border-box", outline: "none",
            }} />
        </div>
        <SearchableSelect
          options={[{ value: "", label: "همه نوعیت‌ها" }, ...Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))]}
          value={typeFilter} onChange={setTypeFilter}
          style={{ minWidth: 160 }}
        />
        {canAdd && (
          <button onClick={() => setShowAdd(true)} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "10px 20px",
            borderRadius: 10, border: "none", cursor: "pointer",
            background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff",
            fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
          }}>
            <Plus size={16} /> ایجاد حساب جدید
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 48, color: T.subtext }}>
          هیچ حسابی یافت نشد
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(acc => {
            const kids      = children(acc.id);
            const isOpen    = expanded.has(acc.id);
            const typeColor = TYPE_COLORS[acc.type] || "#6b7280";

            return (
              <div key={acc.id} style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>

                {/* ردیف مادر */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "28px 160px 1fr 130px 150px 140px",
                  alignItems: "center", gap: 8, padding: "12px 16px",
                  background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  opacity: acc.is_active ? 1 : 0.5,
                }}>
                  {/* expand */}
                  <button onClick={() => kids.length > 0 && toggleExpand(acc.id)}
                    style={{ background: "none", border: "none", cursor: kids.length > 0 ? "pointer" : "default", color: T.subtext, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {kids.length > 0 ? (isOpen ? <ChevronDown size={15} /> : <ChevronLeft size={15} />) : null}
                  </button>

                  {/* نوعیت */}
                  <span style={{
                    padding: "3px 8px", borderRadius: 7, fontSize: 11, fontWeight: 700,
                    background: `${typeColor}18`, color: typeColor, whiteSpace: "nowrap",
                    textAlign: "center",
                  }}>{TYPES[acc.type]}</span>

                  {/* نام */}
                  <span style={{ fontWeight: 800, color: T.text, fontSize: 14 }}>{acc.name}</span>

                  {/* کد */}
                  <span style={{ fontSize: 12, color: T.subtext, fontFamily: "monospace", textAlign: "center" }}>{acc.code}</span>

                  {/* بیلانس */}
                  <span style={{
                    fontWeight: 800, fontSize: 13, textAlign: "left",
                    color: acc.balance >= 0 ? "#10b981" : "#ef4444",
                  }}>{fmt(acc.balance, acc.currency)}</span>

                  {/* دکمه‌ها */}
                  <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                    {canEdit && (
                      <button title="ویرایش" onClick={() => openEditModal(acc)}
                        style={iconBtnStyle("#3b82f6")}><Edit2 size={13} /></button>
                    )}
                    {canEdit && (
                      <button title={acc.is_active ? "مخفی کردن" : "نمایش"} onClick={() => handleToggle(acc)}
                        style={iconBtnStyle(acc.is_active ? "#f59e0b" : "#10b981")}>
                        {acc.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    )}
                    <button title="گزارش" onClick={() => handleReport(acc)} style={iconBtnStyle("#8b5cf6")}><FileText size={13} /></button>
                    {canDelete && (
                      <button title="حذف" onClick={() => handleDelete(acc)} style={iconBtnStyle("#ef4444")}><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>

                {/* زیرمجموعه‌ها */}
                {isOpen && kids.map(child => (
                  <div key={child.id} style={{
                    display: "grid",
                    gridTemplateColumns: "28px 160px 1fr 130px 150px 140px",
                    alignItems: "center", gap: 8,
                    padding: "10px 16px 10px 40px",
                    background: dark ? "rgba(255,255,255,0.02)" : "#fff",
                    borderTop: `1px solid ${T.cardBorder}`,
                    opacity: child.is_active ? 1 : 0.5,
                  }}>
                    {/* indent indicator */}
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: typeColor, display: "inline-block" }} />
                    </span>
                    {/* نوعیت خالی - همتراز با مادر */}
                    <span style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", textAlign: "center" }}>زیرمجموعه</span>
                    {/* نام */}
                    <span style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{child.name}</span>
                    {/* کد */}
                    <span style={{ fontSize: 12, color: T.subtext, fontFamily: "monospace", textAlign: "center" }}>{child.code}</span>
                    {/* بیلانس */}
                    <span style={{ fontWeight: 700, fontSize: 13, color: child.balance >= 0 ? "#10b981" : "#ef4444", textAlign: "left" }}>
                      {fmt(child.balance, child.currency)}
                    </span>
                    {/* دکمه‌ها */}
                    <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                      {canEdit && (
                        <button title="ویرایش" onClick={() => openEditModal(child)}
                          style={iconBtnStyle("#3b82f6")}><Edit2 size={13} /></button>
                      )}
                      {canEdit && (
                        <button title={child.is_active ? "مخفی کردن" : "نمایش"} onClick={() => handleToggle(child)}
                          style={iconBtnStyle(child.is_active ? "#f59e0b" : "#10b981")}>
                          {child.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      )}
                      <button title="گزارش" onClick={() => handleReport(child)} style={iconBtnStyle("#8b5cf6")}><FileText size={13} /></button>
                      {canDelete && (
                        <button title="حذف" onClick={() => handleDelete(child)} style={iconBtnStyle("#ef4444")}><Trash2 size={13} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: ایجاد حساب */}
      {showAdd && (
        <Modal title="ایجاد حساب جدید" onClose={() => setShowAdd(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>نام حساب *</Label>
              {inp(form.name, v => setForm(f => ({ ...f, name: v })), "نام حساب را وارد کنید")}
            </div>
            <div>
              <Label T={T}>نوعیت حساب *</Label>
              <SearchableSelect
                options={Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))}
                value={form.type}
                onChange={v => setForm(f => ({ ...f, type: v, parent_id: "" }))}
                placeholder="انتخاب نوعیت"
              />
            </div>
            <div>
              <Label T={T}>ارز *</Label>
              <SearchableSelect
                options={CURRENCIES.map(c => ({ value: c, label: c }))}
                value={form.currency}
                onChange={v => setForm(f => ({ ...f, currency: v, parent_id: "" }))}
                placeholder="انتخاب ارز"
              />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>حساب مادر (اختیاری)</Label>
              <SearchableSelect
                options={[{ value: "", label: "— حساب اصلی (بدون مادر) —" }, ...parentOptions]}
                value={form.parent_id}
                onChange={v => setForm(f => ({ ...f, parent_id: v }))}
                placeholder="انتخاب حساب مادر"
              />
            </div>
            <div>
              <Label T={T}>بیلانس اولیه</Label>
              {inp(form.opening_balance, v => setForm(f => ({ ...f, opening_balance: v })), "0.00")}
            </div>
            <div>
              <Label T={T}>تاریخ ثبت</Label>
              <PersianDatePicker value={form.created_date} onChange={v => setForm(f => ({ ...f, created_date: v }))} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>توضیحات</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="توضیحات اختیاری..."
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${T.border}`, background: T.input,
                  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                  resize: "vertical", boxSizing: "border-box", outline: "none",
                }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setShowAdd(false)} T={T} />
            <BtnSave onClick={() => submit(handleAdd)} loading={submitting} color="#10b981" label="ایجاد حساب" />
          </div>
        </Modal>
      )}

      {/* Modal: ویرایش */}
      {editAcc && (
        <Modal title={`ویرایش: ${editAcc.name}`} onClose={() => setEditAcc(null)} dark={dark} T={T}>
          {/* هشدار برای حساب‌های قدیمی که هنوز بیلانس اولیه ثبت‌شده ندارند */}
          {editAcc.opening_balance == null && (
            <div style={{
              marginBottom: 14, padding: "10px 14px", borderRadius: 10,
              background: dark ? "rgba(245,158,11,0.1)" : "#fffbeb",
              border: `1.5px solid ${dark ? "rgba(245,158,11,0.3)" : "#fde68a"}`,
              color: dark ? "#fbbf24" : "#92400e", fontSize: 12, lineHeight: 1.7,
            }}>
              ⚠️ این یک حساب قدیمی است و هنوز بیلانس اولیه ثبت‌شده ندارد. اگر می‌خواهید
              بیلانس اولیه را ثبت کنید، چک‌باکس پایین را فعال کنید. در غیر این صورت،
              این فیلد دست نمی‌خورد و گزارش حساب مثل قبل کار می‌کند.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>نام حساب *</Label>
              {inp(editForm.name, v => setEditForm(f => ({ ...f, name: v })))}
            </div>

            <div>
              <Label T={T}>نوعیت حساب *</Label>
              <SearchableSelect
                options={Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))}
                value={editForm.type}
                onChange={v => setEditForm(f => ({ ...f, type: v, parent_id: "" }))}
                placeholder="انتخاب نوعیت"
              />
            </div>

            <div>
              <Label T={T}>ارز *</Label>
              <SearchableSelect
                options={CURRENCIES.map(c => ({ value: c, label: c }))}
                value={editForm.currency}
                onChange={v => setEditForm(f => ({ ...f, currency: v, parent_id: "" }))}
                placeholder="انتخاب ارز"
              />
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>حساب مادر (اختیاری)</Label>
              <SearchableSelect
                options={[{ value: "", label: "— حساب اصلی (بدون مادر) —" }, ...editParentOptions]}
                value={editForm.parent_id}
                onChange={v => setEditForm(f => ({ ...f, parent_id: v }))}
                placeholder="انتخاب حساب مادر"
              />
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>توضیحات</Label>
              <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                rows={2} style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${T.border}`, background: T.input,
                  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                  resize: "vertical", boxSizing: "border-box", outline: "none",
                }} />
            </div>
          </div>

          {/* 🆕 بخش جداگانه برای ویرایش بیلانس اولیه — فقط اگر چک‌باکس فعال باشد */}
          <div style={{
            marginTop: 18, padding: "14px 16px", borderRadius: 12,
            background: editForm.change_opening_balance
              ? (dark ? "rgba(59,130,246,0.08)" : "#eff6ff")
              : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc"),
            border: `1.5px solid ${editForm.change_opening_balance
              ? (dark ? "rgba(59,130,246,0.3)" : "#bfdbfe")
              : T.cardBorder}`,
            transition: "all 0.2s",
          }}>
            <label style={{
              display: "flex", alignItems: "center", gap: 10,
              cursor: "pointer", userSelect: "none",
            }}>
              <input
                type="checkbox"
                checked={editForm.change_opening_balance}
                onChange={e => setEditForm(f => ({ ...f, change_opening_balance: e.target.checked }))}
                style={{
                  width: 18, height: 18, cursor: "pointer",
                  accentColor: "#3b82f6",
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                ✏️ می‌خواهم بیلانس اولیه را تغییر دهم
              </span>
            </label>

            {editForm.change_opening_balance && (
              <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <Label T={T}>بیلانس اولیه جدید</Label>
                  {inp(editForm.opening_balance, v => setEditForm(f => ({ ...f, opening_balance: v })), "0.00")}
                </div>
                <div>
                  <Label T={T}>تاریخ بیلانس اولیه</Label>
                  <PersianDatePicker value={editForm.opening_date} onChange={v => setEditForm(f => ({ ...f, opening_date: v }))} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <div style={{
                    padding: "8px 12px", borderRadius: 8,
                    background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb",
                    border: `1px solid ${dark ? "rgba(245,158,11,0.2)" : "#fde68a"}`,
                    fontSize: 11, color: dark ? "#fbbf24" : "#92400e", lineHeight: 1.7,
                  }}>
                    💡 با ذخیره، اختلاف بین بیلانس اولیه قدیم و جدید به موجودی فعلی <strong>اضافه یا کم</strong> می‌شود (نه دو برابر، نه کپی).
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* اطلاعات راهنما */}
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 10,
            background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff",
            border: `1px solid ${dark ? "rgba(59,130,246,0.2)" : "#dbeafe"}`,
            fontSize: 11, color: dark ? "#93c5fd" : "#1e40af", lineHeight: 1.7,
          }}>
            <div>📊 موجودی فعلی این حساب: <strong>{fmt(editAcc.balance, editAcc.currency)}</strong></div>
            {editAcc.opening_balance != null && (
              <div>🏦 بیلانس اولیه ثبت‌شده: <strong>{fmt(editAcc.opening_balance, editAcc.currency)}</strong></div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditAcc(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره" />
          </div>
        </Modal>
      )}

      {/* Modal: گزارش */}
      {reportAcc && (
        <Modal title={`گزارش حساب: ${reportAcc.name} (${reportAcc.code})`} onClose={() => { setReportAcc(null); setReportDateFrom(""); setReportDateTo(""); }} dark={dark} T={T} wide>

          {/* ── فیلتر تاریخ ── */}
          <div style={{
            marginBottom: 16, padding: "14px 16px", borderRadius: 12,
            background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff",
            border: `1.5px solid ${dark ? "rgba(139,92,246,0.2)" : "#e9d5ff"}`,
          }}>
            {/* دکمه‌های سریع */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8b5cf6", marginLeft: 6 }}>🗓 دوره سریع:</span>
              {[
                { key: "today",  label: "امروز" },
                { key: "week",   label: "۷ روز اخیر" },
                { key: "month",  label: "این ماه" },
                { key: "3month", label: "۳ ماه اخیر" },
                { key: "6month", label: "۶ ماه اخیر" },
                { key: "year",   label: "امسال" },
              ].map(q => (
                <button key={q.key} onClick={() => setQuickDate(q.key)} style={{
                  padding: "5px 12px", borderRadius: 7, border: `1px solid ${dark ? "rgba(139,92,246,0.3)" : "#c4b5fd"}`,
                  background: "transparent", color: "#8b5cf6", cursor: "pointer",
                  fontSize: 11, fontWeight: 600, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}>{q.label}</button>
              ))}
              {(reportDateFrom || reportDateTo) && (
                <button onClick={clearDateFilter} style={{
                  padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`,
                  background: "transparent", color: T.subtext, cursor: "pointer",
                  fontSize: 11, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", marginRight: "auto",
                }}>✕ پاک کردن</button>
              )}
            </div>
            {/* از تاریخ — تا تاریخ */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>از تاریخ</div>
                <PersianDatePicker value={reportDateFrom} onChange={v => { setReportDateFrom(v); }} />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>تا تاریخ</div>
                <PersianDatePicker value={reportDateTo} onChange={v => { setReportDateTo(v); }} />
              </div>
              <button onClick={applyDateFilter} style={{
                padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
                fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                whiteSpace: "nowrap",
              }}>🔍 اعمال فیلتر</button>
            </div>
            {(reportDateFrom || reportDateTo) && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#8b5cf6", fontWeight: 600 }}>
                📌 {reportDateFrom ? `از: ${gregorianToJalaliStr(reportDateFrom)}` : "ابتدا"} — {reportDateTo ? `تا: ${gregorianToJalaliStr(reportDateTo)}` : "انتها"}
                {" | "}{reportData.length} تراکنش
              </div>
            )}
          </div>

          <div id="report-content">
            {/* هدر گزارش */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: 16, color: dark ? "#fff" : "#1e293b" }}>
                گزارش حساب: {reportAcc.name}
              </h2>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: T.subtext }}>کد: <strong style={{ color: T.text }}>{reportAcc.code}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>نوعیت: <strong style={{ color: T.text }}>{TYPES[reportAcc.type]}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>ارز: <strong style={{ color: T.text }}>{reportAcc.currency}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>موجودی فعلی: <strong style={{ color: reportAcc.balance >= 0 ? "#10b981" : "#ef4444" }}>{fmt(reportAcc.balance, reportAcc.currency)}</strong></span>
                <span style={{ fontSize: 11, color: T.subtext }}>
                  تعداد تراکنش: <strong style={{ color: T.text }}>{reportData.length}</strong>
                </span>
              </div>
            </div>

            {reportLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>در حال بارگذاری...</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: dark ? "#1e293b" : "#334155" }}>
                      {["#","تاریخ","شماره سند","نوع معامله","طرف حساب","ارز","مبلغ","مبلغ اصلی × نرخ","معادل USD","توضیح","بیلانس"].map(h => (
                        <th key={h} style={{ padding: "10px 10px", textAlign: "right", fontWeight: 700, color: "#f1f5f9", whiteSpace: "nowrap", fontSize: 11 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                        // اگر بیلانس اولیه از جدول جدید موجود است، از آن استفاده کن
                        // در غیر این صورت (حساب قدیمی)، فرمول قبلی را حفظ کن
                        const openBal = reportAcc
                          ? (reportAcc.opening_balance != null
                              ? Number(reportAcc.opening_balance)
                              : reportAcc.balance - reportData.reduce((s: number, t: any) => {
                                  const a = Math.abs(parseFloat(t.amount || 0));
                                  return t.direction === "in" ? s + a : s - a;
                                }, 0))
                          : 0;
                        let runBal = openBal;
                        return (
                          <>
                            {/* ردیف بیلانس اولیه */}
                            <tr style={{ background: dark ? "rgba(16,185,129,0.08)" : "#f0fdf4", borderBottom: `2px solid #86efac` }}>
                              <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 11 }}>—</td>
                              <td style={{ padding: "8px 10px", color: "#059669", fontSize: 11, fontWeight: 700 }}>
                                {reportAcc?.created_at ? gregorianToJalaliStr(reportAcc.created_at.split(" ")[0]) : "—"}
                              </td>
                              <td style={{ padding: "8px 10px", color: "#059669", fontSize: 11 }}>—</td>
                              <td colSpan={2} style={{ padding: "8px 10px", color: "#059669", fontWeight: 800, fontSize: 12 }}>🏦 بیلانس اولیه حساب</td>
                              <td style={{ padding: "8px 10px", color: T.text, fontSize: 11, textAlign: "center" }}>{reportAcc?.currency}</td>
                              <td style={{ padding: "8px 10px", fontWeight: 800, color: "#059669", fontSize: 13 }}>+{fmt(openBal)}</td>
                              <td colSpan={3} style={{ padding: "8px 10px" }} />
                              <td style={{ padding: "8px 10px", fontWeight: 800, fontSize: 13, color: "#059669" }}>{fmt(openBal)}</td>
                            </tr>
                            {reportData.length === 0 ? (
                              <tr><td colSpan={11} style={{ textAlign: "center", padding: 32, color: T.subtext }}>هیچ تراکنشی ثبت نشده</td></tr>
                            ) : reportData.map((txn: any, i: number) => {
                              const amt = Math.abs(parseFloat(txn.amount || 0));
                              if (txn.direction === "in") runBal += amt; else runBal -= amt;
                              const isForeign = txn.orig_currency && txn.orig_currency !== txn.currency;
                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc") }}>
                                  <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 11 }}>{i+1}</td>
                                  <td style={{ padding: "8px 10px", color: T.text, whiteSpace: "nowrap", fontSize: 11 }}>{gregorianToJalaliStr(txn.date)}</td>
                                  <td style={{ padding: "8px 10px", color: "#8b5cf6", fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>{txn.serial || "—"}</td>
                                  <td style={{ padding: "8px 10px", color: T.text, fontSize: 11 }}>{txn.type}</td>
                                  <td style={{ padding: "8px 10px", color: T.text, fontSize: 11 }}>{txn.party}</td>
                                  <td style={{ padding: "8px 10px", color: T.text, fontSize: 11, textAlign: "center" }}>{txn.currency}</td>
                                  <td style={{ padding: "8px 10px", fontWeight: 700, color: txn.direction === "in" ? "#10b981" : "#ef4444", whiteSpace: "nowrap", fontSize: 12 }}>
                                    {txn.direction === "in" ? "+" : "−"}{fmt(amt)}
                                  </td>
                                  <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 10 }}>
                                    {isForeign ? `${fmt(txn.orig_amount)} ${txn.orig_currency} × ${parseFloat(txn.exchange_rate||1).toFixed(2)}` : "—"}
                                  </td>
                                  <td style={{ padding: "8px 10px", color: "#059669", fontSize: 10 }}>
                                    {txn.customer_amount && txn.customer_currency === "USD" && txn.currency !== "USD" ? `$${fmt(txn.customer_amount)}` : "—"}
                                  </td>
                                  <td style={{ padding: "8px 10px", color: T.subtext, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10 }}>
                                    {txn.notes || "—"}
                                  </td>
                                  <td style={{ padding: "8px 10px", fontWeight: 800, fontSize: 12, color: runBal >= 0 ? "#10b981" : "#ef4444", whiteSpace: "nowrap" }}>
                                    {fmt(runBal)}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button onClick={printReport} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 24px",
              borderRadius: 10, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
              fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            }}>
              <Printer size={15} /> چاپ گزارش
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- کامپوننت‌های کمکی ----

function Modal({ title, onClose, dark, T, children, wide }: any) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: "28px",
        width: "100%", maxWidth: wide ? 920 : 560,
        maxHeight: "88vh", overflow: "auto",
        boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.text }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext, padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Label = ({ T, children }: any) => (
  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.subtext, marginBottom: 6 }}>
    {children}
  </label>
);

const BtnCancel = ({ onClick, T }: any) => (
  <button onClick={onClick} style={{
    padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`,
    background: "transparent", color: T.text, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>انصراف</button>
);

const BtnSave = ({ onClick, loading, color, label }: any) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: "10px 24px", borderRadius: 10, border: "none",
    background: loading ? "#9ca3af" : `linear-gradient(135deg,${color},${color}cc)`,
    color: "#fff", cursor: loading ? "not-allowed" : "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>
    {loading ? "در حال ذخیره..." : label}
  </button>
);

const iconBtnStyle = (color: string): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 8, border: "none",
  background: `${color}18`, color, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
});